import { useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useRole } from "../../contexts/RoleContext";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandHoldingHeart,
  faTruckFast,
  faRepeat,
} from "@fortawesome/free-solid-svg-icons";
import routing from "../../routing";
import { ROLE_COLORS } from "../../constants/roles";
import "../../style/RoleSwitcher.css";

function RoleSwitcher({ isSidebarCollapsed }) {
  const { currentRole, switchRole } = useRole();
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAnimating, setIsAnimating] = useState(false);

  const canDonate = user?.can_donate === 1 || user?.can_donate === true;
  const canReceive = user?.can_receive === 1 || user?.can_receive === true;

  if (!canDonate || !canReceive) {
    return null;
  }

  const handleRoleChange = (newRole) => {
    if (newRole === currentRole) return;

    setIsAnimating(true);
    switchRole(newRole);

    const isOnDonorPage = location.pathname.startsWith(routing.donor.root);
    const isOnReceiverPage = location.pathname.startsWith("/receiver");

    if (newRole === "receiver" && isOnDonorPage) {
      navigate(routing.home.path);
    }

    if (newRole === "donor" && isOnReceiverPage) {
      navigate(routing.home.path);
    }

    setTimeout(() => setIsAnimating(false), 600);
  };

  const currentColors = ROLE_COLORS[currentRole];
  const otherRole = currentRole === "donor" ? "receiver" : "donor";

  // collapsed state
  if (isSidebarCollapsed) {
    return (
      <Box className="role-switcher-collapsed">
        <Tooltip title={t(`Switch to ${otherRole}`)} placement="right">
          <IconButton
            onClick={() => handleRoleChange(otherRole)}
            className={`role-switcher-icon-button ${isAnimating ? "animating" : ""}`}
            sx={{
              width: 48,
              height: 48,
              backgroundColor: currentColors.primary,
              color: "#ffffff",
              boxShadow: `0 4px 12px ${currentColors.primary}40`,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
              overflow: "hidden",
              "&:hover": {
                backgroundColor: currentColors.primary,
                transform: "scale(1.1)",
                boxShadow: `0 6px 16px ${currentColors.primary}60`,
              },
              "&:active": {
                transform: "scale(0.95)",
              },
            }}
          >
            <FontAwesomeIcon
              icon={faRepeat}
              style={{ fontSize: "1.25rem", transition: "transform 0.3s ease" }}
            />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // expanded state
  return (
    <div className="role-switcher-container">
      <div className={`role-toggle ${isAnimating ? "animating" : ""}`}>
        <button
          className={`role-toggle-btn ${currentRole === "donor" ? "active" : ""}`}
          onClick={() => handleRoleChange("donor")}
          aria-label="donor"
        >
          <FontAwesomeIcon icon={faHandHoldingHeart} className="role-toggle-icon" />
          <span>{t("Donor")}</span>
        </button>
        <button
          className={`role-toggle-btn ${currentRole === "receiver" ? "active" : ""}`}
          onClick={() => handleRoleChange("receiver")}
          aria-label="receiver"
        >
          <FontAwesomeIcon icon={faTruckFast} className="role-toggle-icon" />
          <span>{t("Receiver")}</span>
        </button>
      </div>
    </div>
  );
}

export default RoleSwitcher;
